import Component from "@glimmer/component";
import { action } from "@ember/object";
import { task } from "ember-concurrency";
import { tracked, cached } from "@glimmer/tracking";
import { service } from "@ember/service";
import {
  CONNECTION_TYPES,
  getConnectionOptions,
  getConnectionConfig,
  formatSegmentGroup,
  formatNetworkGroup,
} from "glesys-controlpanel/utils/network-utils";

export default class ModalEditNetworkAdapter extends Component {
  @service modal;
  @service cost;
  @service intl;

  @tracked selectedBandwidth = this.networkAdapter.bandwidth;
  @tracked selectedVlanNetwork;
  @tracked selectedSegment;
  @tracked connectionType;
  @tracked internetNetwork;
  @tracked segmentsMap = new Map();
  @tracked vlansMap = new Map();

  get connectionTypes() {
    return CONNECTION_TYPES;
  }

  get project() {
    return this.server.get("project");
  }

  get networkAdapter() {
    return this.args.params.networkAdapter;
  }

  get server() {
    return this.networkAdapter.server;
  }

  get task() {
    return this.args.params.task;
  }

  get isSliderDisabled() {
    return this.connectionType === CONNECTION_TYPES.PRIVATE;
  }

  @cached
  get connectionConfig() {
    return getConnectionConfig({
      selectedSegment: this.selectedSegment,
      selectedVlanNetwork: this.selectedVlanNetwork,
      cost: this.cost,
      server: this.server,
      selectedBandwidth: this.selectedBandwidth,
      connectionType: this.connectionType,
    });
  }

  get isSubmitDisabled() {
    return !this.connectionConfig.isValid();
  }

  get networkAdapterCost() {
    return this.connectionConfig.getCost();
  }

  get connectionOptions() {
    const options = getConnectionOptions(this.connectionType);
    return options.map((option) => ({
      ...option,
      label: this.intl.t(option.labelKey),
    }));
  }

  get bandwidthOptions() {
    return this.connectionConfig.getBandwidthOptions();
  }

  constructor() {
    super(...arguments);
    this.initializeNetworkState();
  }

  initializeNetworkState() {
    const { network, segment } = this.networkAdapter;

    const type = this.networkAdapter.networkConnectionType;

    if (type === CONNECTION_TYPES.VLAN) {
      this.selectedVlanNetwork = network;
    } else if (type === CONNECTION_TYPES.INTERNET) {
      this.internetNetwork = network;
    } else {
      this.selectedSegment = segment;
    }

    this.connectionType = type;
  }

  loadSegments = task(async () => {
    const privateNetworks = await this.server.project.privateNetworks;
    const segmentsGroupedByPrivateNetworks = await Promise.all(
      privateNetworks.map(async (privateNetwork) => {
        const filteredSegments = (await privateNetwork.segments).filter(
          (segment) => segment.platform === "vmware" && this.server.datacenter === segment.dataCenter.name,
        );
        filteredSegments.forEach((segment) => {
          this.segmentsMap.set(segment.id, segment);
        });
        return formatSegmentGroup(privateNetwork, filteredSegments);
      }),
    );
    const segmentGroups = segmentsGroupedByPrivateNetworks.filter((group) => group.options.length > 0);
    if (!this.selectedSegment && segmentGroups.length > 0) {
      const firstSegmentId = segmentGroups[0].options[0].value;
      this.selectedSegment = this.segmentsMap.get(firstSegmentId);
    }
    return segmentGroups;
  });

  async getNetworksByPrefix(prefix, single = false) {
    const networks = await this.project.get("networks");
    const serverDatacenter = this.server.get("datacenter");
    const filteredNetworks = networks.filter(
      (network) => network.id.startsWith(prefix) && network.dataCenter === serverDatacenter,
    );
    if (single) return filteredNetworks[0];
    filteredNetworks.forEach((network) => {
      this.vlansMap.set(network.id, network);
    });
    if (!this.selectedVlanNetwork && filteredNetworks.length > 0) {
      const firstVlanId = filteredNetworks[0].id;
      this.selectedVlanNetwork = this.vlansMap.get(firstVlanId);
    }
    return formatNetworkGroup(filteredNetworks);
  }

  loadNetworks = task(async () => this.getNetworksByPrefix("vl"));
  loadInternetNetwork = task(async () => this.getNetworksByPrefix("internet", true));

  submitTask = task(async () => {
    let payload = {
      bandwidth: this.selectedBandwidth,
      segment: null,
      network: null,
    };

    if (this.connectionType === CONNECTION_TYPES.PRIVATE) {
      payload.segment = this.selectedSegment;
    } else if (this.connectionType === CONNECTION_TYPES.VLAN) {
      payload.network = this.selectedVlanNetwork;
    } else {
      payload.network = this.internetNetwork;
    }
    await this.task.perform(payload);
    this.modal.close();
  });

  @action
  async handleFormChange({ connectionType, segment, network }) {
    if (connectionType && connectionType !== this.connectionType) {
      this.connectionType = connectionType;

      if (connectionType === CONNECTION_TYPES.INTERNET && !this.internetNetwork) {
        this.internetNetwork = await this.loadInternetNetwork.perform();
      }

      const initialConnectionType = this.networkAdapter.networkConnectionType;

      if (connectionType === initialConnectionType) {
        this.selectedBandwidth = this.networkAdapter.bandwidth;
      } else {
        this.selectedBandwidth = this.bandwidthOptions[1];
      }
    }
    if (segment) {
      const selectedSegment = this.segmentsMap.get(segment);
      if (selectedSegment) {
        this.selectedSegment = selectedSegment;
      }
    }
    if (network) {
      const selectedVlanNetwork = this.vlansMap.get(network);
      if (selectedVlanNetwork) {
        this.selectedVlanNetwork = selectedVlanNetwork;
      }
    }
  }

  @action
  selectBandwidth(bandwidth) {
    this.selectedBandwidth = bandwidth;
  }
}
